/* global gapi, YT */
/**
 * Google Api max results per page
 * YT (pl, pl items, search results) - 50
 * Google Drive - 1000
 */
import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/browser';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import appendScriptTag from '../../gUtilities/appendScriptTag';
import YouTubePlaylistItemWithDuration from '../../models/gapi/gapiPlaylistItemExtensions';
import UploadResponse from '../../models/gapi/uploadResponse';
import mimeTypes from '../../models/mimeTypes';
import QueryIterator from '../../models/queryIterator';
import { StorageProviderKey } from '../../models/storageProviderKeys';
import { Keys, messages, Tasks } from '../../settings';
import compress, { decompress } from '../../utilities/flateCompressDecompress';
import { MediaUploader } from '../../utilities/gapiUpload';
import IStorageProvider from '../storage/istorageProvider';
import { Context } from '../_globalContext/context';
import * as gis from './gisClient';
import GapiTokenService from './gapiTokenService';
import handleGapiError from './handleGapiError';

const apiKey = 'AIzaSyAmmBn3Rbfigj6yReisACUyFMYJiPsKNH4';
const clientId = '755034602295-fcqcgdol81clacgt1giu25j295n7hkah.apps.googleusercontent.com';
/** How long to let gapi client init run before telling the user it is stuck. */
const gapiClientInitTimeoutMs = 20000;

export enum GoogleScope {
  YouTube = 'https://www.googleapis.com/auth/youtube.readonly',
  DriveReadonly = 'https://www.googleapis.com/auth/drive.readonly',
  AppData = 'https://www.googleapis.com/auth/drive.appdata',
}
export enum GoogleMimeTypes {
  File = 'application/vnd.google-apps.file',
  Folder = 'application/vnd.google-apps.folder',
}
export interface IGoogleApiContext {
  iframeApiLoaded: boolean;
  dataApiLoaded: boolean;
  gapiClientLoaded: boolean;
  gapiAuthorized: boolean;
  authorizedScopes: string;

  playerEl: React.RefObject<HTMLIFrameElement>;
  ytPlayer: React.MutableRefObject<YT.Player | undefined>;

  searchPaged(q: any, maxResults?: number):
    Promise<QueryIterator<gapi.client.youtube.SearchResult>>;
  getUserPlaylistsPaged(maxResults?: number):
    Promise<QueryIterator<gapi.client.youtube.Playlist>>;
  getPlaylistItemsPaged(playlistId: string, maxResults?: number):
    Promise<QueryIterator<YouTubePlaylistItemWithDuration>>;
  getUserDrive(folder: string, resultsPerPage: number):
    Promise<QueryIterator<gapi.client.drive.File>>;
  getDriveFolder(fileId: string):
    gapi.client.Request<gapi.client.drive.File>;
  getFilesInFolderRecursive(folder: string, maxResults?: number):
    Promise<gapi.client.drive.File[]>;
  getDriveFileUrl(fileId: string, alt?: string, overrideChecks?: boolean):
    Promise<string>;
  getPlaylist(playlistId: string):
    Promise<gapi.client.youtube.PlaylistItemListResponse>;
  getDuration(...videoIds: string[]):
    Promise<gapi.client.Response<gapi.client.youtube.VideoListResponse>>;

  resetAppDataFolder(): Promise<void>;
  downloadAppDataFolder(): Promise<void>;

  playVideo(
    callback?: () => void,
    onStateChange?: (ev: YT.OnStateChangeEvent) => void,
    onError?: (ev: YT.OnErrorEvent) => void,
  ): void;

  /**
   * Resolves on the popup path and never settles on the redirect path, which
   * navigates away. Only callers passing `allowRedirect` can take that path,
   * and work that must follow sign-in belongs in `intent`.
   */
  authorize(options?: {
    scope?: string;
    allowRedirect?: boolean;
    intent?: gis.RedirectIntent;
  }): Promise<void>;
  authorizeAdditionalScope(scope: string): Promise<unknown>;
  /** An interactive sign-in is in flight. Buttons show it and refuse a second press. */
  isAuthorizing: boolean;
  /**
   * Whether the current token can read the user's own Drive files.
   *
   * `drive.appdata` is not enough and is easy to mistake for enough: it grants
   * the hidden application folder where the library and playlists live, and
   * nothing else. A token holding only that reads settings perfectly well and
   * is refused on every music file.
   */
  canReadDriveFiles: boolean;
  /**
   * Whether interactive authorisation can happen without leaving the page.
   *
   * True where a popup works. False where authorising means a full-page
   * redirect - which is fine from a button, and not fine from an effect the
   * user did not knowingly trigger, because it tears the app down.
   */
  canAuthorizeInPlace: boolean;
  logout(): void;
  disconnect(): void;
  /** The current Google token, or undefined when not authorised. */
  getAccount(): gis.GoogleToken | undefined;
}
export const GoogleApiContext = React.createContext({} as IGoogleApiContext);

interface Props {
  children: React.ReactNode;
}

export default function GapiService({
  children,
}: Props) {

  const ctx = useContext(Context);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  /**
   * Caches the drive ids of app data files
   * Behaves like a ref
   * 
   * useState is quicker over useRef, erronously all methods below request this obj
   * as dependency which is unneeded.
   */
  const [appDataIdsCache] = useState({} as any);
  const sp = useRef<IStorageProvider>();
  const getAppDataIdsPromise = useRef<Promise<gapi.client.drive.File[]>>();
  const [gapiTokenService] = useState(() => new GapiTokenService());

  /**
   * Root element for the youtube iframe player
   */
  const playerEl = useRef<HTMLIFrameElement>(null);
  const ytPlayer = useRef<YT.Player>();

  const [iframeApiLoaded, setIframeApiLoaded] = useState(false);
  const [dataApiLoaded, setDataApiLoaded] = useState(false);
  const [gapiClientLoaded, setGapiClientLoaded] = useState(false);
  /**
   * Currently this flag doesn't say much
   * Since it's possible to use Google Drive w/o using it as the AppData storage provider
   * 
   * It is set upon successful authorization, 
   * verified by testing for the profile scope
   */
  const [gapiAuthorized, setGapiAuthorized] = useState(false);

  // Never includes base appdata scope, we track gapiAuthorized for that
  const [authorizedScopes, setAuthorizedScopes] = useState<string>('');

  // Read from the token, not from authorizedScopes - that string deliberately
  // omits appdata and so cannot answer this on its own. Every path that
  // changes the token also moves gapiAuthorized or authorizedScopes, so a
  // plain render-time read stays current.
  const canReadDriveFiles = gis.hasScope(GoogleScope.DriveReadonly);

  // Platform-constant, but kept alongside the scope flag because call sites
  // need both to decide between "ask now" and "tell the user what to press".
  const canAuthorizeInPlace = !gis.shouldUseRedirect();


  /**
   * Scopes are cumulative: GIS issues a token for exactly what is asked for,
   * so requesting one scope silently drops any already held. Always ask for
   * everything granted so far plus whatever is new.
   */
  const scopesToRequest = useCallback(function (scope?: string) {
    // Fall back to what was granted in an earlier session. Without this, the
    // first request after a reload asks only for the one scope in hand and
    // Google issues a token narrowed to it - silently dropping consent the
    // user already gave.
    const held = (gis.grantedScopes() || gis.rememberedScopes())
      .split(' ')
      .filter(Boolean);

    if (scope && held.indexOf(scope) === -1) {
      held.push(scope);
    }

    if (held.length === 0) {
      held.push(GoogleScope.AppData);
    }

    return held.join(' ');
  }, []);

  /**
   * Must be called straight from a user gesture. Do not await anything first.
   *
   * On iOS this never resolves - it navigates the page to Google and the app
   * is torn down mid-promise. So do not chain work onto it that has to happen
   * after sign-in; describe that work in `intent` instead, and the startup
   * effect replays it when the token comes back.
   */
  const doAuthorize = useCallback(async function (
    options?: { scope?: string; allowRedirect?: boolean; intent?: gis.RedirectIntent },
  ) {
    const token = await gis.requestToken({
      scope: scopesToRequest(options?.scope),
      interactive: true,
      allowRedirect: options?.allowRedirect,
      intent: options?.intent,
    });

    // The old gapi.auth2 path could report success having established nothing,
    // which produced an endless sign-in loop. A token either came back with
    // the scope on it or it did not.
    if (options?.scope !== undefined
      && token.scope.split(' ').indexOf(options.scope) === -1) {
      throw new Error(messages(Keys.googleScopeNotGranted));
    }

    setGapiAuthorized(true);
    setAuthorizedScopes(token.scope);
  }, [scopesToRequest]);

  const [isAuthorizing, setAuthorizing] = useState(false);
  const authorizeInFlight = useRef<Promise<void>>();

  /**
   * One interactive sign-in at a time - the same shape oneDriveService and
   * dropboxService have, for the same reason.
   *
   * gisClient already refuses a second request while one is pending, but it
   * refuses it with an error, and an error is the wrong answer to a user
   * pressing the button twice because nothing visibly happened the first
   * time. The second caller gets the first promise instead, and isAuthorizing
   * lets the button say so.
   *
   * On the redirect path the promise never settles and the flag never clears,
   * deliberately: the page is going away, and "Signing in…" is the truth
   * until it does.
   */
  const authorize = useCallback(function (
    options?: { scope?: string; allowRedirect?: boolean; intent?: gis.RedirectIntent },
  ): Promise<void> {

    if (authorizeInFlight.current) {
      return authorizeInFlight.current;
    }

    setAuthorizing(true);

    authorizeInFlight.current = doAuthorize(options).finally(function settled() {
      authorizeInFlight.current = undefined;
      setAuthorizing(false);
    });

    return authorizeInFlight.current;
  }, [doAuthorize]);

  /**
   * Both live callers are sign-in buttons - the Drive browser asking for
   * drive.readonly, and the YouTube page asking for youtube.readonly - so the
   * redirect is allowed here for the same reason it is on the setup screen.
   * Without it these buttons took the popup path on iOS, which is the one that
   * does not work, and the extra scope could never be granted there at all.
   *
   * No intent is needed: beginRedirect remembers the current path, so the user
   * lands back on the page whose button they pressed.
   */
  const authorizeAdditionalScope = useCallback(async function (scope: GoogleScope) {
    await authorize({ scope, allowRedirect: true });
  }, [authorize]);

  const logout = useCallback(function logout() {

    function completeLogout() {
      gis.revokeToken();
      setGapiAuthorized(false);
      setAuthorizedScopes('');
    }

    if (ctxRef.current.storageProvider.current === sp.current) {
      ctxRef.current.configureStorageProvider();
      ctxRef.current.spLogoutHandling().then(completeLogout);
    }
    else {
      completeLogout();
    }
  }, []);

  /**
   * Revoking the token withdraws the app's access outright, the same as
   * removing it from https://myaccount.google.com/permissions
   *
   * Under GIS this is exactly what logout already does - there is no session
   * left over to sign out of separately.
   */
  const disconnect = useCallback(function disconnect() {
    logout();
  }, [logout]);

  /**
   * GIS issues access tokens and nothing else - there is no profile attached,
   * so the signed-in email is no longer available without spending a further
   * scope and a userinfo round trip. Callers show a generic connected state.
   */
  const getAccount = useCallback(function () {
    return gis.getToken();
  }, []);

  const errorHandler = useCallback(
    handleGapiError(ctxRef.current.callSnackbar, logout),
    [logout],
  );

  useEffect(function registerAsStorageProvider() {
    ctxRef.current.storageProviderMap[StorageProviderKey.GoogleDrive] = sp;
  }, []);

  /**
   * Authorisation state follows the token, rather than only being set at the
   * moment we ask for one.
   *
   * GIS tokens last about an hour and cannot be silently renewed where
   * third-party cookies are blocked, so they really do lapse mid-session.
   * Without this the UI would go on claiming the user was signed in while
   * every Drive request came back 401.
   */
  useEffect(function trackTokenState() {
    return gis.onTokenChange(token => {
      setGapiAuthorized(!!token);
      setAuthorizedScopes(token?.scope ?? '');
    });
  }, []);

  useEffect(function loadScripts() {
    appendScriptTag(
      "https://www.youtube.com/iframe_api",
      undefined,
      () => setIframeApiLoaded(true),
      (event: Event | string,
        _source?: string,
        _lineno?: number,
        _colno?: number,
        error?: Error,
      ) => ctxRef.current.callSnackbar(error || event),
    );
    appendScriptTag(
      "https://apis.google.com/js/api.js",
      undefined,
      () => setDataApiLoaded(true),
      (event: Event | string,
        _source?: string,
        _lineno?: number,
        _colno?: number,
        error?: Error,
      ) => ctxRef.current.callSnackbar(error || event),
    );

    gis.configureGis(clientId);

    // Subscribe before anything can publish a token, so gapi.client is never
    // the last to know.
    gapiTokenService.start();

    // A redirect sign-in reloads the page, so a token obtained seconds ago
    // arrives as storage rather than as a callback. Pick it up before anything
    // concludes the user is signed out - and before GIS is even known to load,
    // since the redirect route never needed it.
    const restored = gis.restorePersistedToken();

    if (restored) {
      setGapiAuthorized(true);
      setAuthorizedScopes(restored.scope);
    }

    // The redirect intent is deliberately NOT replayed here. sp.current is
    // assigned by updateStorageProvider, which is declared far below this and
    // therefore runs after it on mount - so configureStorageProvider called
    // from here always found nothing registered and said so. See
    // replayRedirectIntent, which is placed after that effect for this reason.

    // Failing to load GIS is not fatal: everything except Google keeps working,
    // and authorize() will report it if the user asks for Google.
    gis.loadGis()
      .then(() => {
        // Already holding a token - a silent request here would be pure risk.
        if (restored) {
          return undefined;
        }

        // Where GIS cannot be quiet it opens a window instead, and doing that
        // during startup prompts the user before the app has rendered and
        // backgrounds the page mid-initialisation. Skip it; sign-in is a
        // button, and on these platforms it always was.
        if (!gis.canRenewSilently()) {
          return undefined;
        }

        // Nothing remembered means this browser has never signed in to
        // Fadila, so there is nothing to renew - and GIS does even a silent
        // renewal through a popup window. Opened from a page load rather than
        // a click, that popup is the browser's to block, and on a first visit
        // it was: the only thing the attempt produced was a "popup blocked"
        // badge in the address bar before the user had pressed anything.
        // The sign-in button is a click, and its popup is allowed.
        const remembered = gis.rememberedScopes();

        if (!remembered) {
          return undefined;
        }

        // Try for a token without bothering the user. This succeeds where the
        // browser still sends Google's cookies and fails on Safari, which is
        // expected and not worth reporting - the sign-in button covers it.
        return gis.requestToken({
          // Ask for everything previously consented to, not just appdata.
          // Asking narrowly here is what left a returning user signed in but
          // unable to read a single one of their own Drive files.
          scope: remembered,
          interactive: false,
        }).then(
          token => {
            setGapiAuthorized(true);
            setAuthorizedScopes(token.scope);
          },
          () => { /* user will sign in explicitly */ },
        );
      })
      .catch(err => console.warn('Google Identity Services unavailable', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Determines if the external google libraries have been loaded.
   * When loaded we can proceed to initializing the google api client.
   */
  useEffect(function shouldInitClient() {
    /**
     * Set once initialisation reaches any conclusion, success or failure.
     * The watchdog below uses it to tell "still working" from "wedged".
     */
    let settled = false;

    /**
     * One retry before giving up.
     *
     * gapi.client.load fetches discovery documents over the network and offers
     * no timeout of its own, so a request that dies in flight leaves it waiting
     * forever - and with it the whole loading checklist, since gapiClientLoaded
     * never flips. iOS kills in-flight requests routinely: any moment the page
     * spends backgrounded during startup will do it. Warning about that and
     * then doing nothing left the app hung on a screen the user cannot get past.
     */
    let retried = false;
    let watchdog: ReturnType<typeof setTimeout>;

    function retryOrWarnClientInit() {
      if (settled) {
        return;
      }

      if (!retried) {
        retried = true;
        watchdog = setTimeout(retryOrWarnClientInit, gapiClientInitTimeoutMs);

        try {
          initClient();
        }
        catch (err) {
          settled = true;
          errorHandler(err);
        }

        return;
      }

      ctxRef.current.callSnackbar(
        'Google client API has not finished initialising. '
        + 'The discovery documents for Drive and YouTube could not be loaded.',
      );
    }

    watchdog = setTimeout(retryOrWarnClientInit, gapiClientInitTimeoutMs);

    async function initClient() {
      /**
       * Initialize the google api client with correct api key and YouTube data api v3
       */
      gapi.load('client', async () => {
        // Everything in here must be caught locally. This callback is async and
        // is invoked by gapi, so a rejection escaping it is an unhandled
        // rejection nobody sees - and since gapiClientLoaded then never flips,
        // the app sits on the loading screen forever with no error at all.
        try {
          gapi.client.setApiKey(apiKey);
          await gapi.client.load('youtube', 'v3');
          await gapi.client.load('drive', 'v3');

          // Authorisation is deliberately NOT part of this gate any more.
          //
          // gapi.auth2.init() used to run here, and startup blocked until it
          // resolved - so a browser that would not let it confirm a session
          // (any Safari, thanks to ITP) hung the whole app on the loading
          // screen, even for people whose library lives in OneDrive or
          // Dropbox and who never needed Google at all.
          //
          // GIS has no init step and no session to restore. The client is
          // ready once the discovery documents are in; a token is requested
          // later, from a user gesture, and only when Google is actually used.

          // The silent token attempt races this, and gapi.client cannot be
          // given a token before it exists. If that race was won by the token,
          // hand it over now - otherwise gapi would make unauthenticated calls
          // until the next token change.
          const existingToken = gis.getAccessToken();

          if (existingToken) {
            gapi.client.setToken({ access_token: existingToken });
          }

          settled = true;
          setGapiClientLoaded(true);
        }
        catch (err) {
          settled = true;
          errorHandler(err);
        }
      });
    }

    if (dataApiLoaded) {
      try {
        initClient();
      } catch (err) {
        settled = true;
        errorHandler(err);
      }
    }
    else {
      // Nothing started, so nothing to warn about yet.
      settled = true;
    }

    return function cancelWatchdog() {
      clearTimeout(watchdog);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataApiLoaded]);

  const searchPaged = useCallback(function (q: any, maxResults?: number) {
    if (!gapiClientLoaded) {
      ctxRef.current.callSnackbar('Unable to access google api, client not ready');
      return Promise.reject();
    }

    let pageToken: string | undefined;

    function doSearch(q: string, _maxResults?: number, pageToken?: string,) {
      return gapi.client.youtube.search.list({
        q,
        part: 'snippet',
        fields: 'items(id,snippet(thumbnails/default,title,channelTitle)),nextPageToken,pageInfo/totalResults',
        maxResults: 50,
        pageToken,
      });
    }

    return (function nextPagedResultsClosure() {

      return (async function nextPagedResults(
        maxResults?: number,
      ): Promise<QueryIterator<gapi.client.youtube.SearchResult>> {
        const res = await doSearch(q, maxResults, pageToken);

        pageToken = res.result.nextPageToken;

        // The returned object holds the result of the first execution and
        // allows for further execution
        return {
          results: res.result.items || [],
          totalResults: res.result.pageInfo!.totalResults || 0,
          next: res.result.nextPageToken ? nextPagedResults : undefined,
        };
      })(maxResults);
    })();
  }, [gapiClientLoaded]);

  /**
   * 
   * @param maxResults 
   * Update: Fetching less than the displayed per page creates
   * a silly UX where you press next page but it just lenghtens the list
   * so disregard the following..
   * 
   * We fetch greedily despite only displaying a portion of returned results
   * Therefore we usually leave this option undefined and leave the api to decide the result count
   */
  const getUserPlaylistsPaged = useCallback(function (maxResults?: number) {

    if (!gapiClientLoaded) {
      ctxRef.current.callSnackbar('Unable to access google api, client not ready');
      return Promise.reject();
    }
    if (!gapiAuthorized) {
      ctxRef.current.callSnackbar('User not authorized for request');
      return Promise.reject();
    }

    let pageToken: string | undefined;

    function gapiGetPlaylists(_maxResults?: number, pageToken?: string) {
      return gapi.client.youtube.playlists.list({
        part: 'snippet,contentDetails',
        maxResults: 50,
        fields: 'items(id,snippet(thumbnails/default,title),contentDetails/itemCount),nextPageToken,pageInfo/totalResults',
        mine: true,
        pageToken,
      });
    }

    return (function nextPagedResultsClosure() {

      return (async function nextPagedResults(
        maxResults?: number,
      ): Promise<QueryIterator<gapi.client.youtube.Playlist>> {

        const res = await gapiGetPlaylists(maxResults, pageToken);

        pageToken = res.result.nextPageToken;

        // The returned object holds the result of the first execution and
        // allows for further execution
        return {
          results: res.result.items || [],
          totalResults: res.result.pageInfo!.totalResults || 0,
          next: res.result.nextPageToken ? nextPagedResults : undefined,
        };
      })(maxResults);
    })();
  }, [gapiClientLoaded, gapiAuthorized]);

  const getPlaylist = useCallback(async function (playlistId: string, _maxResults?: number, pageToken?: string) {
    if (!gapiClientLoaded) {
      ctxRef.current.callSnackbar('Unable to access google api, client not ready');
      return Promise.reject();
    }

    const res = await gapi.client.youtube.playlistItems.list({
      part: 'snippet,contentDetails',
      maxResults: 50,
      playlistId,
      fields: 'items(contentDetails/videoId,snippet(description,thumbnails/default,title,publishedAt)),nextPageToken,pageInfo/totalResults',
      pageToken,
    });

    return res.result;

  }, [gapiClientLoaded]);
  const getDuration = useCallback(async function (...videoIds: string[]) {
    return await gapi.client.youtube.videos.list({
      part: 'contentDetails',
      fields: 'items(id,contentDetails/duration)',
      id: videoIds.join(','),
      maxResults: 50,
    });
  }, []);

  /**
   * 
   * @param maxResults 
   */
  const getPlaylistItemsPaged = useCallback(function (playlistId: string, maxResults?: number) {

    if (!gapiClientLoaded) {
      ctxRef.current.callSnackbar('Unable to access google api, client not ready');
      return Promise.reject();
    }

    let pageToken: string | undefined;

    return (function nextPagedResultsClosure() {

      return (async function nextPagedResults(
        maxResults?: number,
      ): Promise<QueryIterator<YouTubePlaylistItemWithDuration>> {

        const res = await getPlaylist(playlistId, maxResults, pageToken);

        // update items with duration
        if (res.items) {
          const durations = await getDuration(
            ...(res.items.map(itm => itm.contentDetails!.videoId!))
          );

          if (durations.result.nextPageToken) {
            Sentry.captureMessage(
              'found nextPageToken in durations.result. Indicates that we have to refactor getDuration in getPlaylistItemsPaged. We were assuming getDuration had same max results.',
              Severity.Error);
          }

          res.items = res.items.map(itm => {
            const matchingDuration
              = durations.result.items?.find(
                durItm => durItm.id === itm.contentDetails!.videoId!);

            if (matchingDuration?.contentDetails?.duration) {
              (itm as YouTubePlaylistItemWithDuration).duration
                = matchingDuration?.contentDetails?.duration;

            }

            return itm;
          });
        }

        pageToken = res.nextPageToken;

        // The returned object holds the result of the first execution and
        // allows for further execution
        return {
          results: res.items || [],
          totalResults: res.pageInfo!.totalResults || 0,
          next: res.nextPageToken ? nextPagedResults : undefined,
        };
      })(maxResults);
    })();
  }, [gapiClientLoaded, getPlaylist, getDuration]);

  const gapiGetFiles = useCallback(function (folder: string, maxResults?: number, pageToken?: string) {
    return gapi.client.drive.files.list({
      q: `'${folder}' in parents and trashed = false`,
      pageSize: Math.min(1000, Math.max(100, maxResults || 1)),
      fields: "files(iconLink,id,mimeType,name,size),nextPageToken",
      pageToken,
    });
  }, []);

  /**
   * Get GoogleDrive folder files
   * @param folder 
   * @param maxResults 
   */
  const getUserDrive = useCallback(async function (folder: string, maxResults?: number) {
    if (!gapiClientLoaded) {
      ctxRef.current.callSnackbar('Unable to access google api, client not ready');
      return Promise.reject();
    }
    if (!gapiAuthorized) {
      ctxRef.current.callSnackbar('User not authorized for request');
      return Promise.reject();
    }

    let pageToken: string | undefined;

    return (function nextPagedResultsClosure() {

      return (async function nextPagedResults(
        maxResults?: number,
      ): Promise<QueryIterator<gapi.client.drive.File>> {
        const res = await gapiGetFiles(folder, maxResults, pageToken);

        pageToken = res.result.nextPageToken;

        // The returned object holds the result of the first execution and
        // allows for further execution
        return {
          results: res.result.files || [],
          next: res.result.nextPageToken ? nextPagedResults : undefined,
        };
      })(maxResults);
    })();
  }, [gapiClientLoaded, gapiAuthorized, gapiGetFiles]);

  const getFilesInFolderRecursive = useCallback(async function (folder: string) {

    const maxResults = 1000;
    const files: gapi.client.drive.File[] = [];
    const allFolders: gapi.client.drive.File[] = [];
    let pageToken: string | undefined;

    do {
      var iter = await gapiGetFiles(folder, maxResults, pageToken);

      pageToken = iter.result.nextPageToken;
      if (iter.result.files) {
        const folders = iter.result.files.filter(x => x.mimeType === 'application/vnd.google-apps.folder');
        allFolders.push(...folders);
        const audioItems = iter.result.files.filter(x => mimeTypes.has(x.mimeType!))
        files.push(...audioItems);
      }
    } while (iter.result.nextPageToken);

    for (let folder of allFolders) {
      const fil = await getFilesInFolderRecursive(folder.id!);
      files.push(...fil);
    }

    return files;
  }, [gapiGetFiles]);

  const getDriveFolder = useCallback(function (fileId: string) {
    if (!gapiClientLoaded) {
      ctxRef.current.callSnackbar('Unable to access google api, client not ready');
      throw new Error('Unable to access google api, client not ready');
    }
    if (!gapiAuthorized) {
      ctxRef.current.callSnackbar('User not authorized for request');
      throw new Error('User not authorized for request');
    }

    return gapi.client.drive.files.get({
      fileId,
      fields: 'parents,name',
    });
  }, [gapiClientLoaded, gapiAuthorized]);

  /**
   * Gets the drive file url set Html Audio element src to
   * 
   * @param fileId Google Drive unique file id
   * @param alt Used to control download, see Google APi
   * @param overrideChecks When player triggers authorization we don't want to wait for state to update before calling getDriveFileUrl
   */
  const getDriveFileUrl = useCallback(async function (fileId: string, alt = 'media', overrideChecks = false) {

    // Checked even when overrideChecks is set: that flag exists to skip the
    // *authorization state* checks during the player's auth flow, not this.
    //
    // The URL returned below carries no credentials at all. The service worker
    // is the only thing that turns it into an authenticated request, and the
    // only consumer that needs that is <audio> playback, which cannot set
    // headers itself. Without the worker every play is a 401, so fail loudly
    // here rather than letting the player sit and spin.
    //
    // The appdata reads in getOrCreateDataFile used to come through here as
    // well, which made the entire startup depend on the worker. They set
    // their own Authorization now and never touch this function.
    if (!ctxRef.current.deviceCapabilities.hasServiceWorkers) {
      ctxRef.current.callSnackbar(messages(Keys.driveRequiresServiceWorker));
      return Promise.reject(messages(Keys.driveRequiresServiceWorker));
    }

    if (!overrideChecks) {
      if (!gapiClientLoaded) {
        ctxRef.current.callSnackbar('Unable to access google api, client not ready');
        return Promise.reject();
      }
      if (!gis.hasScope(GoogleScope.AppData) && !gis.hasScope(GoogleScope.DriveReadonly)) {
        ctxRef.current.callSnackbar('User not authorized for request');
        return Promise.reject();
      }
    }

    // The URL carries no credentials. The service worker attaches the
    // Authorization header on the way out - see thesw.js - because the <audio>
    // element cannot set headers itself.
    if (!gis.getAccessToken()) {
      ctxRef.current.callSnackbar(messages(Keys.storageProviderNotLoggedIn));
      return Promise.reject(messages(Keys.storageProviderNotLoggedIn));
    }

    return `https://www.googleapis.com/drive/v3/files/${fileId}?alt=${alt}`;

    // Scopes are read live from gisClient rather than captured here, so they
    // are deliberately not dependencies.
  }, [gapiClientLoaded]);

  const putAppDataFile = useCallback(async function doPutAppDataFile(
    name: string,
    file: any,
  ) {
    // Uploads authenticate themselves rather than going through the service
    // worker, so the token has to be present here and now.
    const accessToken = gis.getAccessToken();

    if (!accessToken) {
      return Promise.reject(messages(Keys.storageProviderNotLoggedIn));
    }

    var prevFileId = appDataIdsCache[name];

    if (!(file instanceof Blob)) {

      const compressed = compress(file, ctxRef.current.flate);
      file = new Blob(
        [compressed],
        { type: 'application/x-gzip' }
      );
    }

    const res: any = await new Promise(function (resolve, reject) {
      var uploader = new MediaUploader({
        file,
        token: accessToken,
        metadata: {
          name,
          parents: prevFileId ? undefined : ['appDataFolder'],
        },
        fileId: prevFileId,
        onComplete: resolve,
        onError: reject,
      });
      uploader.upload();
    });

    const jsonResponse: UploadResponse = JSON.parse(res);
    appDataIdsCache[jsonResponse.name] = jsonResponse.id;

  }, [appDataIdsCache]);

  const getAppDataIds = useCallback(async function (resetAppData?: boolean) {

    async function doGetAppDataIds() {
      const files = new Array<gapi.client.drive.File>();
      let pageToken: string | undefined;

      do {
        var res = await gapi.client.drive.files.list({
          spaces: 'appDataFolder',
          fields: 'nextPageToken, files(id, name)',
          pageSize: 1000,
          pageToken,
        });

        pageToken = res.result.nextPageToken;
        if (res.result.files) {
          files.push(...res.result.files);
        }
      } while (res.result.nextPageToken);

      // Sort files by name
      files.sort((a, b) => a.name! < b.name! ? -1 : a.name === b.name ? 0 : 1);

      // If resetting, don't warn
      if (!resetAppData) {
        let prev;

        // We used to have alot of issues with duplicate app data files in GDrive
        for (let file of files) {
          if (prev && file && prev.name === file.name) {
            ctxRef.current.callSnackbar(messages(Keys.googleAppDataDuplicates));
            break;
          }

          prev = file;
        }
      }

      files.forEach(
        file => appDataIdsCache[file.name!] = file.id
      );

      return files;
    }

    // One request is enough
    if (!resetAppData && getAppDataIdsPromise.current) {
      try {
        await getAppDataIdsPromise.current;
        return;
      } catch (err) { } // Don't handle, just try again
    }

    getAppDataIdsPromise.current = doGetAppDataIds();
    return await getAppDataIdsPromise.current;

  }, [appDataIdsCache]);

  const getOrCreateDataFile = useCallback(async function (
    fileName: string,
    defaultData: any,
  ) {
    try {
      if (!appDataIdsCache[fileName]) {
        await getAppDataIds();
      }

      if (appDataIdsCache[fileName]) {
        // Authenticate here, not through the service worker. The worker
        // exists for <audio>, which cannot set headers; a fetch can, and
        // the upload path below already does. Routing these three reads
        // through the worker meant the whole startup - settings, library,
        // playlists - died with a 403 whenever the worker was not in the
        // path: a hard reload runs the page uncontrolled, and at least one
        // ad blocker stopped it intercepting altogether. The worker sees a
        // request that already carries Authorization and leaves it be.
        const accessToken = gis.getAccessToken();

        if (!accessToken) {
          throw messages(Keys.storageProviderNotLoggedIn);
        }

        const resp = await fetch(
          `https://www.googleapis.com/drive/v3/files/${appDataIdsCache[fileName]}?alt=media`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );

        if (resp.ok) {
          const buffer = await resp.arrayBuffer();
          return decompress(buffer, ctxRef.current.flate);
        }
        else {
          throw resp;
        }
      }
      else {
        await putAppDataFile(fileName, defaultData);
        return defaultData;
      }
    }
    catch (err) {
      errorHandler(err);
      return Promise.reject();
    }
  }, [errorHandler, putAppDataFile, appDataIdsCache, getAppDataIds]);

  const resetAppDataFolder = useCallback(async function () {
    ctxRef.current.setRunningTask(Tasks.resetAppDataFolder);

    try {
      const files = await getAppDataIds(true);

      if (files) {
        for (let file of files) {
          await gapi.client.drive.files.delete({
            fileId: file.id!
          });
        }
      }

      try {
        localStorage.clear();
      } catch { }
    } finally {
      ctxRef.current.removeRunningTask(Tasks.resetAppDataFolder);
    }

    window.location.reload();

  }, [getAppDataIds]);

  const downloadAppDataFolder = useCallback(async function () {
    ctxRef.current.setRunningTask(Tasks.downloadAppDataFolder);

    try {
      const files = await getAppDataIds(true);

      if (files) {

        var temporaryDownloadLink = document.createElement("a");
        temporaryDownloadLink.style.display = 'none';

        document.body.appendChild(temporaryDownloadLink);

        for (const file of files) {

          const accessToken = gis.getAccessToken();

          const res = await fetch(
            `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`, {
            headers: new Headers({
              'Authorization': `Bearer ${accessToken}`
            })
          });

          const blob = await res.blob();
          var objUrl = window.URL.createObjectURL(blob);


          temporaryDownloadLink.setAttribute('href', objUrl);
          temporaryDownloadLink.setAttribute('download', file.name!);

          temporaryDownloadLink.click();
        }

        document.body.removeChild(temporaryDownloadLink);
      }
    }
    finally {
      ctxRef.current.removeRunningTask(Tasks.downloadAppDataFolder);
    }
  }, [getAppDataIds]);

  // saveFile() lived here: it created Music/Podcasts folders and uploaded into
  // them, and was the only caller that ever asked for GoogleScope.Drive - full
  // read/write over the user's entire Drive, the most restricted scope Google
  // publishes. It was dead code (never wired into the storage provider below),
  // and a restricted scope costs an annual third-party security assessment at
  // OAuth verification time, so it is deleted rather than left dormant. If
  // writing to Drive is ever wanted, do it with drive.file and the Picker.

  useEffect(function updateStorageProvider() {
    sp.current = {
      name: StorageProviderKey.GoogleDrive,
      load: (fileName: string, defaultData: any) => {
        // Read the token directly rather than the React state captured when this
        // effect last ran. configureStorageProvider is called straight after
        // authorize() resolves, but effects are deferred until after paint - so
        // the first load() ran against a closure that still said "not
        // authorised", reset the provider, and bounced the user back to the
        // sign-in screen. Endlessly, once GIS made the token arrive on the
        // click rather than during startup.
        if (gis.hasScope(GoogleScope.AppData)) {
          return getOrCreateDataFile(fileName, defaultData);
        }
        else {
          ctxRef.current.configureStorageProvider();
          return Promise.reject(messages(Keys.storageProviderNotLoggedIn));
        }
      },
      // save: not supported. The implementation it used to call (saveFile) is
      // gone along with the full-drive scope it needed - see the note above.
      saveAppData: (fileName: string, fileData: any) => {
        // Uploads go through gapi.client, which authenticates itself, so this
        // would otherwise succeed without the worker - and that is the danger.
        // If the read failed we are holding default/empty state, and writing
        // that back would destroy the user's real file.
        if (!ctxRef.current.deviceCapabilities.hasServiceWorkers) {
          return Promise.reject(messages(Keys.driveRequiresServiceWorker));
        }
        // Read the token directly rather than the React state captured when this
        // effect last ran. configureStorageProvider is called straight after
        // authorize() resolves, but effects are deferred until after paint - so
        // the first load() ran against a closure that still said "not
        // authorised", reset the provider, and bounced the user back to the
        // sign-in screen. Endlessly, once GIS made the token arrive on the
        // click rather than during startup.
        if (gis.hasScope(GoogleScope.AppData)) {
          return putAppDataFile(fileName, fileData)
        }
        else {
          ctxRef.current.configureStorageProvider();
          return Promise.reject(messages(Keys.storageProviderNotLoggedIn));
        }
      },
    };
  }, [gapiAuthorized, authorizedScopes, getOrCreateDataFile, putAppDataFile]);

  /**
   * Finish what a redirect sign-in started.
   *
   * Placement matters more than the contents. React runs effects in the order
   * they are declared, and `sp.current` is assigned by updateStorageProvider
   * directly above - so this has to sit after it, or configureStorageProvider
   * finds an unregistered provider and reports exactly that. The same trap the
   * comment inside updateStorageProvider describes, one level up.
   */
  useEffect(function replayRedirectIntent() {
    // gapiClientLoaded, not just gapiAuthorized. Coming back from the redirect
    // the token is read straight out of localStorage, so "authorised" is true
    // within a tick of mounting - while apis.google.com/js/api.js is still in
    // flight. Configuring Drive then immediately reads the library through
    // gapi.client.drive, and the reward for being early is
    // "Can't find variable: gapi".
    if (!gapiAuthorized || !gapiClientLoaded) {
      return;
    }

    const intent = gis.consumeRedirectIntent();

    if (intent && intent.configureProvider) {
      ctxRef.current.configureStorageProvider(StorageProviderKey.GoogleDrive);
    }
  }, [gapiAuthorized, gapiClientLoaded]);

  const playVideo = useCallback(function (
    callback?: () => void,
    onStateChange?: (ev: YT.OnStateChangeEvent) => void,
    onError?: (ev: YT.OnErrorEvent) => void,
  ) {

    if (!iframeApiLoaded) {
      ctxRef.current.callSnackbar('Unable to play video, YouTube iFrame Api is not ready');
      throw new Error('Unable to play video, YouTube iFrame Api is not ready');
    }
    if (!playerEl.current) {
      ctxRef.current.callSnackbar('Unable to play video, iframe not found');
      throw new Error('Unable to play video, iframe not found');
    }

    // I don't know what it is that this constructor creates 
    // but it bears no resemblance to playerEvt.target
    new YT.Player(playerEl.current, {
      events: {
        onReady: playerEvt => {
          ytPlayer.current = playerEvt.target;
          playerEvt.target.playVideo();

          if (callback) callback();
        },
        onStateChange,
        onError,
      }
    });
  }, [iframeApiLoaded]);

  const ctxValue = useMemo(() => ({
    iframeApiLoaded,
    dataApiLoaded,
    gapiClientLoaded,
    gapiAuthorized,
    authorizedScopes,

    playerEl,
    ytPlayer,

    searchPaged,
    getUserPlaylistsPaged,
    getPlaylistItemsPaged,
    getUserDrive,
    getDriveFolder,
    getFilesInFolderRecursive,
    getDriveFileUrl,
    getPlaylist,
    getDuration,

    resetAppDataFolder,
    downloadAppDataFolder,

    playVideo,

    authorize,
    authorizeAdditionalScope,
    isAuthorizing,
    canReadDriveFiles,
    canAuthorizeInPlace,
    logout,
    disconnect,
    getAccount,
  }), [
    canReadDriveFiles,
    canAuthorizeInPlace,
    iframeApiLoaded,
    dataApiLoaded,
    gapiClientLoaded,
    gapiAuthorized,
    authorizedScopes,
    playerEl,
    ytPlayer,
    searchPaged,
    getUserPlaylistsPaged,
    getPlaylistItemsPaged,
    getUserDrive,
    getDriveFolder,
    getFilesInFolderRecursive,
    getDriveFileUrl,
    getPlaylist,
    getDuration,

    resetAppDataFolder,
    downloadAppDataFolder,
    playVideo,
    authorize,
    authorizeAdditionalScope,
    isAuthorizing,
    logout,
    disconnect,
    getAccount,
  ]);

  return (
    <GoogleApiContext.Provider value={ctxValue}>
      {children}
    </GoogleApiContext.Provider>
  );
};
